import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { AuthClient } from "@nav-games/auth-client";
import {
  type Attempt,
  type Difficulty,
  type SessionResult,
  SessionRecorder,
  isPremium,
  recordAttempt,
  pickWeightedKey,
  statsFor,
} from "@nav-games/games-core";
import { ITU_CODE, patternToTimeline, play } from "@nav-games/morse";
import { Torch } from "@nav-games/torch";

import { HelpButton, HelpPanel, InlineReference } from "../../ui/HelpPanel";
import { PremiumGate } from "../../ui/PremiumGate";

interface FlashcardsProps {
  auth: AuthClient;
  onNavigate: (to: string) => void;
}

const MODULE = "morse";
const GAME = "flashcards";
const CARDS_PER_SESSION = 20;
const BEAT_THE_CLOCK_MS = 60_000;
const CHOICES_PER_CARD = 6;
const PLAYBACK_WPM = 15;

type CardMode = "visual" | "audio" | "light";
type SessionMode = "cards" | "timed";

function poolFor(difficulty: Difficulty): readonly string[] {
  const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
  const digits = "0123456789".split("");
  const punct = [".", ",", "?", "/", "+", "-", "="];
  switch (difficulty) {
    case "beginner":
      return letters;
    case "intermediate":
      return [...letters, ...digits];
    case "expert":
      return [...letters, ...digits, ...punct];
  }
}

interface Card {
  answer: string;
  pattern: string;
  choices: readonly string[];
}

function nextCard(pool: readonly string[], recent: readonly string[]): Card {
  const answer = pickWeightedKey(MODULE, pool, recent);
  const pattern = ITU_CODE[answer]!;
  const distractors = new Set<string>();
  while (distractors.size < CHOICES_PER_CARD - 1) {
    const d = pool[Math.floor(Math.random() * pool.length)]!;
    if (d !== answer) distractors.add(d);
  }
  const choices = shuffle([answer, ...distractors]);
  return { answer, pattern, choices };
}

function shuffle<T>(input: readonly T[]): T[] {
  const arr = [...input];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const ai = arr[i]!;
    const aj = arr[j]!;
    arr[i] = aj;
    arr[j] = ai;
  }
  return arr;
}

function renderPattern(pattern: string): string {
  return pattern.replace(/\./g, "•").replace(/-/g, "—");
}

interface Settings {
  difficulty: Difficulty;
  cardMode: CardMode;
  sessionMode: SessionMode;
}

export function Flashcards({ auth, onNavigate }: FlashcardsProps) {
  const [settings, setSettings] = useState<Settings | null>(null);
  const premium = isPremium();

  if (!settings) {
    return (
      <Picker
        auth={auth}
        premium={premium}
        onStart={setSettings}
        onBack={() => onNavigate("/play/morse")}
      />
    );
  }
  return (
    <Game
      settings={settings}
      onAgain={() => setSettings({ ...settings })}
      onBack={() => onNavigate("/play/morse")}
    />
  );
}

interface PickerProps {
  auth: AuthClient;
  premium: boolean;
  onStart: (s: Settings) => void;
  onBack: () => void;
}

function Picker({ auth, premium, onStart, onBack }: PickerProps) {
  const [difficulty, setDifficulty] = useState<Difficulty>("beginner");
  const [cardMode, setCardMode] = useState<CardMode>("visual");
  const [sessionMode, setSessionMode] = useState<SessionMode>("cards");
  const [helpOpen, setHelpOpen] = useState(false);

  return (
    <>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          gap: 8,
        }}
      >
        <h2 style={{ margin: 0 }}>Morse flashcards</h2>
        <HelpButton onOpen={() => setHelpOpen(true)} />
      </div>
      <p className="muted">
        Each round shows a Morse pattern (visual, audio or flashing
        light). Pick the matching character from six choices.
      </p>
      <HelpPanel
        section={helpOpen ? "morse" : null}
        onClose={() => setHelpOpen(false)}
      />

      <div className="card">
        <h3>Level</h3>
        <div
          className="row"
          style={{ flexDirection: "column", gap: 8, alignItems: "stretch" }}
        >
          <ToggleButton
            selected={difficulty === "beginner"}
            onClick={() => setDifficulty("beginner")}
          >
            Beginner — letters only
          </ToggleButton>
          <ToggleButton
            selected={difficulty === "intermediate"}
            disabled={!premium}
            onClick={() => premium && setDifficulty("intermediate")}
          >
            Intermediate — letters + digits {!premium && "🔒"}
          </ToggleButton>
          <ToggleButton
            selected={difficulty === "expert"}
            disabled={!premium}
            onClick={() => premium && setDifficulty("expert")}
          >
            Expert — + punctuation {!premium && "🔒"}
          </ToggleButton>
        </div>
      </div>

      <div className="card">
        <h3>How is the pattern shown?</h3>
        <div
          className="row"
          style={{ flexDirection: "column", gap: 8, alignItems: "stretch" }}
        >
          <ToggleButton
            selected={cardMode === "visual"}
            onClick={() => setCardMode("visual")}
          >
            Visual — read the dots and dashes
          </ToggleButton>
          <ToggleButton
            selected={cardMode === "audio"}
            onClick={() => setCardMode("audio")}
          >
            Audio — hear the Morse tone
          </ToggleButton>
          <ToggleButton
            selected={cardMode === "light"}
            onClick={() => setCardMode("light")}
          >
            Light — Aldis-lamp flashes
          </ToggleButton>
        </div>
      </div>

      <div className="card">
        <h3>Session length</h3>
        <div
          className="row"
          style={{ flexDirection: "column", gap: 8, alignItems: "stretch" }}
        >
          <ToggleButton
            selected={sessionMode === "cards"}
            onClick={() => setSessionMode("cards")}
          >
            {CARDS_PER_SESSION} cards
          </ToggleButton>
          <ToggleButton
            selected={sessionMode === "timed"}
            onClick={() => setSessionMode("timed")}
          >
            Beat the clock — {Math.round(BEAT_THE_CLOCK_MS / 1000)} seconds
          </ToggleButton>
        </div>
      </div>

      {!premium && <PremiumGate auth={auth} />}

      <div
        className="row"
        style={{ gap: 8, flexDirection: "column", alignItems: "stretch" }}
      >
        <button onClick={() => onStart({ difficulty, cardMode, sessionMode })}>
          Start
        </button>
        <button className="secondary" onClick={onBack}>
          ← Back to Morse
        </button>
      </div>
    </>
  );
}

function ToggleButton({
  selected,
  disabled,
  onClick,
  children,
}: {
  selected: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      className={selected ? "" : "secondary"}
      disabled={disabled}
      onClick={onClick}
      style={{
        textAlign: "left",
      }}
    >
      {children}
    </button>
  );
}

interface GameProps {
  settings: Settings;
  onAgain: () => void;
  onBack: () => void;
}

function Game({ settings, onAgain, onBack }: GameProps) {
  const { difficulty, cardMode, sessionMode } = settings;
  const pool = useMemo(() => poolFor(difficulty), [difficulty]);
  // Recent answers kept out of the next draw so the spaced-repetition
  // picker doesn't resurface the same card within a short window.
  // Clamped to pool size - 1 so there is always an eligible card.
  const avoidWindow = Math.min(8, pool.length - 1);
  const recentRef = useRef<string[]>([]);
  const recorder = useMemo(
    () =>
      new SessionRecorder({
        module: MODULE,
        game: GAME,
        length: sessionMode === "cards" ? CARDS_PER_SESSION : 0,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [settings],
  );

  const [index, setIndex] = useState(0);
  const [card, setCard] = useState<Card>(() => {
    const c = nextCard(pool, []);
    recentRef.current = [c.answer];
    return c;
  });
  const [shownAt, setShownAt] = useState<number>(() => Date.now());
  const [feedback, setFeedback] = useState<"right" | "wrong" | null>(null);
  const [result, setResult] = useState<SessionResult | null>(null);
  const sessionStartedAt = useRef<number>(Date.now());

  const [now, setNow] = useState<number>(Date.now());
  useEffect(() => {
    if (sessionMode !== "timed" || result) return;
    const t = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(t);
  }, [sessionMode, result]);
  const timeLeftMs = Math.max(
    0,
    BEAT_THE_CLOCK_MS - (now - sessionStartedAt.current),
  );

  // End the session when timer hits zero.
  useEffect(() => {
    if (sessionMode !== "timed" || result) return;
    if (timeLeftMs === 0) setResult(recorder.summarise());
  }, [timeLeftMs, sessionMode, result, recorder]);

  const advance = useCallback(() => {
    if (sessionMode === "cards" && index + 1 >= CARDS_PER_SESSION) {
      setResult(recorder.summarise());
      return;
    }
    setIndex((i) => i + 1);
    const next = nextCard(pool, recentRef.current);
    recentRef.current = [...recentRef.current, next.answer].slice(-avoidWindow);
    setCard(next);
    setShownAt(Date.now());
    setFeedback(null);
  }, [avoidWindow, index, pool, recorder, sessionMode]);

  const onChoose = useCallback(
    (choice: string) => {
      if (feedback || result) return;
      const ms = Date.now() - shownAt;
      const ok = choice === card.answer;
      const attempt: Omit<Attempt, "module"> = {
        char: card.answer,
        ok,
        ms,
        ts: Date.now(),
      };
      recorder.log(attempt);
      recordAttempt({ ...attempt, module: MODULE });
      setFeedback(ok ? "right" : "wrong");
      setTimeout(advance, ok ? 400 : 900);
    },
    [advance, card, feedback, recorder, result, shownAt],
  );

  if (result) {
    return (
      <EndCard
        result={result}
        pool={pool}
        difficulty={difficulty}
        onAgain={onAgain}
        onBack={onBack}
      />
    );
  }

  const liveCorrect = recorder.summarise().correct;
  const header =
    sessionMode === "cards"
      ? `Card ${index + 1} of ${CARDS_PER_SESSION} · ${difficulty}`
      : `${Math.ceil(timeLeftMs / 1000)} s left · ${liveCorrect} right · ${difficulty}`;

  return (
    <>
      <p className="small muted">{header}</p>
      <div className="card" style={{ textAlign: "center" }}>
        <CardDisplay
          card={card}
          mode={cardMode}
          feedback={feedback}
          keyOnNew={`${index}-${card.answer}`}
        />
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 8,
          }}
        >
          {card.choices.map((c) => (
            <button
              key={c}
              className="secondary"
              disabled={!!feedback}
              onClick={() => onChoose(c)}
              style={{
                fontSize: 20,
                padding: "12px 0",
                background:
                  feedback && c === card.answer
                    ? "rgba(44, 122, 59, 0.18)"
                    : undefined,
              }}
            >
              {c}
            </button>
          ))}
        </div>
      </div>
      {difficulty === "beginner" && <InlineReference section="morse" />}
    </>
  );
}

/// One of three renderers per card:
///
/// - visual: large dot-and-dash text.
/// - audio:  silent until the user taps "▶"; auto-plays once on
///           mount so they don't always have to.
/// - light:  a circle that flashes on/off following the pattern's
///           Morse timeline. Auto-plays + tap to replay.
function CardDisplay({
  card,
  mode,
  feedback,
  keyOnNew,
}: {
  card: Card;
  mode: CardMode;
  feedback: "right" | "wrong" | null;
  keyOnNew: string;
}) {
  const colour =
    feedback === "right"
      ? "var(--ok, #2c7a3b)"
      : feedback === "wrong"
        ? "var(--danger, #b8302a)"
        : undefined;

  if (mode === "visual") {
    return (
      <div
        style={{
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
          fontSize: 56,
          letterSpacing: "0.08em",
          margin: "16px 0 24px",
          color: colour,
        }}
      >
        {renderPattern(card.pattern)}
      </div>
    );
  }
  if (mode === "audio") {
    return <AudioCard key={keyOnNew} card={card} colour={colour} />;
  }
  return <LightCard key={keyOnNew} card={card} colour={colour} />;
}

function AudioCard({ card, colour }: { card: Card; colour: string | undefined }) {
  const timeline = useMemo(
    () => patternToTimeline(card.pattern, PLAYBACK_WPM),
    [card.pattern],
  );
  const audioCtxRef = useRef<AudioContext | null>(null);
  const doPlay = useCallback(() => {
    if (!audioCtxRef.current) audioCtxRef.current = new AudioContext();
    play(timeline, { audioContext: audioCtxRef.current });
  }, [timeline]);

  useEffect(() => {
    const t = setTimeout(doPlay, 200);
    return () => clearTimeout(t);
  }, [doPlay]);

  return (
    <div style={{ margin: "24px 0", color: colour }}>
      <button onClick={doPlay} style={{ fontSize: 24, padding: "16px 32px" }}>
        ▶ Replay tone
      </button>
    </div>
  );
}

function LightCard({ card, colour }: { card: Card; colour: string | undefined }) {
  const timeline = useMemo(
    () => patternToTimeline(card.pattern, PLAYBACK_WPM),
    [card.pattern],
  );
  const [lit, setLit] = useState(false);
  // Torch availability is asked once on mount — if the device
  // doesn't have a flash unit (web, iPad, desktop) we hide the
  // toggle entirely.
  const [torchAvailable, setTorchAvailable] = useState(false);
  const [useTorch, setUseTorch] = useState(false);
  useEffect(() => {
    let cancelled = false;
    Torch.isAvailable()
      .then((r) => {
        if (!cancelled) setTorchAvailable(r.available);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const doFlash = useCallback((): (() => void) => {
    let cancelled = false;
    let handle = 0;
    function step(i: number) {
      if (cancelled || i >= timeline.length) {
        setLit(false);
        if (useTorch) void Torch.set({ on: false }).catch(() => {});
        return;
      }
      const seg = timeline[i]!;
      setLit(seg.on);
      if (useTorch) void Torch.set({ on: seg.on }).catch(() => {});
      handle = window.setTimeout(() => step(i + 1), seg.durationMs);
    }
    step(0);
    return () => {
      cancelled = true;
      window.clearTimeout(handle);
      setLit(false);
      if (useTorch) void Torch.set({ on: false }).catch(() => {});
    };
  }, [timeline, useTorch]);

  useEffect(() => {
    const cancel = doFlash();
    return cancel;
  }, [doFlash]);

  return (
    <div
      style={{
        margin: "20px 0 24px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 12,
      }}
    >
      <div
        onClick={() => doFlash()}
        role="button"
        tabIndex={0}
        aria-label="Replay Morse light pattern"
        style={{
          width: 120,
          height: 120,
          borderRadius: "50%",
          background: lit ? "#ffd84d" : "#1b2230",
          border: "3px solid #3a4854",
          boxShadow: lit ? "0 0 32px 8px rgba(255, 216, 77, 0.7)" : "none",
          transition: "background 30ms linear, box-shadow 30ms linear",
          cursor: "pointer",
        }}
      />
      <button
        className="secondary"
        onClick={() => doFlash()}
        style={{ color: colour }}
      >
        Replay flashes
      </button>
      {torchAvailable && (
        <label
          className="small muted"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            cursor: "pointer",
          }}
        >
          <input
            type="checkbox"
            checked={useTorch}
            onChange={(e) => setUseTorch(e.target.checked)}
          />
          Aldis-lamp mode (uses device torch)
        </label>
      )}
    </div>
  );
}

interface EndCardProps {
  result: SessionResult;
  pool: readonly string[];
  difficulty: Difficulty;
  onAgain: () => void;
  onBack: () => void;
}

function EndCard({ result, pool, difficulty, onAgain, onBack }: EndCardProps) {
  const rolling = pool
    .map((c) => ({ char: c, ...statsFor(MODULE, c) }))
    .filter((s) => s.attempts >= 3 && s.hitRate < 1)
    .sort((a, b) => a.hitRate - b.hitRate);
  const weakest = rolling.slice(0, 5);

  return (
    <>
      <h2>Session complete</h2>
      <div className="card">
        <p>
          <strong>{result.correct}</strong> / {result.total} correct
          {result.total > 0 && (
            <> ({Math.round((result.correct / result.total) * 100)}%)</>
          )}.
        </p>
        {result.total > 0 && (
          <p className="small muted">
            Mean response: {Math.round(result.meanMs)} ms.
          </p>
        )}
      </div>
      {weakest.length > 0 && (
        <div className="card">
          <h3>Weakest characters (rolling)</h3>
          <ul className="small muted">
            {weakest.map((w) => (
              <li key={w.char}>
                <code>{w.char}</code> — {Math.round(w.hitRate * 100)}% over{" "}
                {w.attempts} attempts
              </li>
            ))}
          </ul>
        </div>
      )}
      <div
        className="row"
        style={{ gap: 8, flexDirection: "column", alignItems: "stretch" }}
      >
        <button onClick={onAgain}>Play another ({difficulty})</button>
        <button className="secondary" onClick={onBack}>
          ← Back to Morse
        </button>
      </div>
    </>
  );
}
